/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Checkbox, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { SelectAutocomplete } from "components/forms";
import { useActionHook, useModals } from "hooks";
import { IApiServiceError } from "store";
import { useDealActions } from "store/actions-context";
import { IAssetClass } from "store/types";

import { useManageAssetClassesContext } from ".";

interface Props {
  onSuccess: (data: IAssetClass) => void;
  onCancel: () => void;
  formRef?: React.RefObject<HTMLDivElement>;
}

type Options = {
  id: number;
  name: string;
};

export const CreateAssetClass: React.FC<Props> = ({ onSuccess, onCancel, formRef }) => {
  const { getDealsFiltersAction } = useDealActions();
  const { getSubAssetName } = useManageAssetClassesContext();
  const { createAssetClassManagementAction } = useDealActions();
  const { deleteModal, errorModal } = useModals();

  const [displayName, setDisplayName] = useState<string>("");
  const [options, setOptions] = useState<Options[]>([]);
  const [value, setValue] = useState<Options[]>([]);

  const createSubAssetClass = async () => {
    const name = await getSubAssetName(() => null);

    if (name) {
      const newOption = { id: options.length + 1, name };
      setOptions((prev) => [...prev, newOption]);
      setValue((prev) => [...prev, newOption]);
    }
  };

  const { action: handleSubmit, loading = true } = useActionHook(
    async () =>
      createAssetClassManagementAction({
        name: displayName,
        is_hidden: false,
        sub_asset_classes: value.map((item) => ({
          name: item.name,
          is_hidden: false
        }))
      }),
    {
      onSuccess: (data) => {
        onSuccess(data);
        getDealsFiltersAction();
      },
      onReject: (error) => {
        const errors = Object.values((error as IApiServiceError).errors || {});
        errorModal({
          msg: errors.length ? errors.flat().join("\n ") : (error as IApiServiceError).message
        });
      }
    }
  );

  const handleDeleteSubAssetClass = (id: number) => {
    deleteModal({
      element: "Delete sub asset class",
      message: `Are you sure you want to delete this sub asset class - "${options.find((item) => item.id === id)?.name}"?`,
      action: async () => {
        setOptions((prev) => prev.filter((item) => item.id !== id));
        setValue((prev) => prev.filter((item) => item.id !== id));
      }
    });
  };

  return (
    <Stack sx={{ flexDirection: "row", alignItems: "center", p: "8px 0", gap: "12px" }} ref={formRef}>
      <TextField
        sx={{
          flex: "20%",
          "& .MuiInputBase-root": { height: "32px" },
          minWidth: 216
        }}
        value={displayName}
        onChange={(e) => {
          setDisplayName(e.target.value);
        }}
      />
      <SelectAutocomplete
        value={value}
        disableClearable
        sx={{
          flex: "80%",
          "& .MuiInputBase-root": {
            pt: "2px !important",
            pb: "2px !important"
          }
        }}
        multiple
        selectAll
        disableCloseOnSelect
        options={options}
        getOptionLabel={(option) => option.name}
        compareParam={(option) => option.name}
        createNew={{
          title: "Add New Sub Asset Class",
          buttonProps: {
            startIcon: <AddIcon />,
            onMouseDown: createSubAssetClass
          }
        }}
        renderOption={(props, option) => {
          const isSelected = Boolean(props["aria-selected"]);
          return (
            <li {...props}>
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  height: 36,
                  width: "100%"
                }}
              >
                <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
                  <Checkbox
                    disabled
                    checked={isSelected}
                    sx={{
                      "&.Mui-checked": {
                        color: "#071F35",
                        "&.Mui-disabled": {
                          color: "#071F35"
                        }
                      },
                      "&.MuiCheckbox-root": {
                        color: "#071F35",
                        "&.Mui-disabled": {
                          color: "#071F35"
                        }
                      }
                    }}
                  />
                  <Typography>{option.name}</Typography>
                  <PersonOutlineIcon />
                </Stack>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSubAssetClass(option.id);
                  }}
                  size="sm"
                >
                  <BinDeleteIcon />
                </IconButton>
              </Stack>
            </li>
          );
        }}
        setValue={(value) => {
          setValue(value);
        }}
        renderInput={(params) => <TextField {...params} placeholder="Select sub asset classes" />}
      />
      <IconButton sx={{ color: "#26A6AE" }} onClick={handleSubmit} disabled={loading || !displayName.length}>
        {loading ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
      </IconButton>
      <IconButton sx={{ color: "#92A7BC" }} onClick={onCancel} disabled={loading}>
        {loading ? <CircularProgress size={20} /> : <ClearIcon />}
      </IconButton>
    </Stack>
  );
};
